import { createModel } from '@rematch/core';
import type { RootModel } from '.';
import { generateRestAPI } from '../helpers/helpers';
import { getData } from '../services/commonService';
import { IOverviewVehicleAlerts, initialValues } from '../type/overview-types';

async function runWithConcurrencyLimit(
	tasks: Array<() => Promise<unknown>>,
	limit: number,
): Promise<void> {
	let cursor = 0;
	const worker = async () => {
		while (cursor < tasks.length) {
			const i = cursor++;
			try {
				await tasks[i]();
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error('[overview] chart API failed:', err);
			}
		}
	};
	const workerCount = Math.min(limit, tasks.length);
	await Promise.all(Array.from({ length: workerCount }, () => worker()));
}

export const overview = createModel<RootModel>()({
	state: {
		...initialValues,
		// Cache key for chart APIs — survives tab navigation in Redux
		lastFetchedKey: '' as string,
		// KPI tile fetch flag — once true, tiles never re-fetch on date change or tab return
		kpiFetched: false as boolean,
	},
	reducers: {
		setActiveVehicles(state, res) {
			return { ...state, activeVehicles: res };
		},
		setActiveAlertsCount(state, res) {
			return { ...state, activeAlertsCount: res };
		},
		setAvgFuelEfficiencyCount(state, res) {
			return { ...state, avgFuelEfficiencyCount: res };
		},
		setFleetUtilizationPercent(state, res) {
			return { ...state, fleetUtilizationPercent: res };
		},
		setDistanceCovered(state, res) {
			return { ...state, distanceCovered: res };
		},
		setTripDistanceKm(state, res) {
			return { ...state, tripDistanceKm: res };
		},
		setFleetUtilizationRate(state, res) {
			return { ...state, fleetUtilizationRate: res };
		},
		setIdleTimeMin(state, res) {
			return { ...state, idleTimeMin: res };
		},
		setStopDuration(state, res) {
			return { ...state, stopDuration: res };
		},
		setNoOfTrips(state, res) {
			return { ...state, noOfTrips: res };
		},
		setVehicleAlertsV2(state, res) {
			return { ...state, vehicleAlertsV2: res };
		},
		setDistanceDrivenKm(state, res) {
			return { ...state, distanceDrivenKm: res };
		},
		setVehicleTypeUtilization(state, res) {
			return { ...state, vehicleTypeUtilization: res };
		},
		setFuelEnergyTiles(state, res) {
			return { ...state, fuelEnergyTiles: res };
		},
		setFuelConsumptionChart(state, res) {
			return { ...state, fuelConsumptionChart: res };
		},
		setIdleFuelWasteChart(state, res) {
			return { ...state, idleFuelWasteChart: res };
		},
		setDriverScores(state, res) {
			return { ...state, driverScores: res };
		},
		setEcoDriverScore(state, res) {
			return { ...state, ecoDriverScore: res };
		},
		setDriveVsIdle(state, res) {
			return { ...state, driveVsIdle: res };
		},
		setDriveVsIdleAllDrivers(state, res) {
			return { ...state, driveVsIdleAllDrivers: res };
		},
		setDriverNameFilter(state, res) {
			return { ...state, driverNameFilter: res };
		},
		setDashcamAlertEvents(state, res) {
			return { ...state, dashcamAlertEvents: res };
		},
		setObdAlertEvents(state, res) {
			return { ...state, obdAlertEvents: res };
		},
		setDashcamAlertSeverity(state, res) {
			return { ...state, dashcamAlertSeverity: res };
		},
		setMaintenanceStatusV2(state, res) {
			return { ...state, maintenanceStatusV2: res };
		},
		setMaintServiceOverdue(state, res) {
			return { ...state, maintServiceOverdue: res };
		},
		setDateRangeFilterPrev(state, res) {
			return { ...state, dateRangeFilterFromStorePrev: res };
		},
		setOverviewVehicleAlerts(state, res) {
			return { ...state, overviewVehicleAlerts: res };
		},
		setTemperature(state, res) {
			return { ...state, temperature: res };
		},
		// Chart API cache key — cleared when date picker changes
		setLastFetchedKey(state, key: string) {
			return { ...state, lastFetchedKey: key };
		},
		// KPI tile flag — set true after first fetch, never reset except on logout
		setKpiFetched(state, val: boolean) {
			return { ...state, kpiFetched: val };
		},
		clearStore() {
			return { ...initialValues, lastFetchedKey: '', kpiFetched: false };
		},
	},

	effects: (dispatch) => ({
		// ─────────────────────────────────────────────────────────────────────
		// fetchChartApis — called on every date picker change.
		// On FIRST load (kpiFetched=false): fires ALL 24 APIs — KPI tiles +
		//   charts. On DATE CHANGE (kpiFetched=true): fires only 17 chart
		//   APIs — KPI tiles are skipped, they use a fixed yesterday→today
		//   window so they never need to reload when the date picker changes.
		// All calls fire CONCURRENTLY (Promise.allSettled) instead of one at
		//   a time. Each tile/chart writes to its own slice of Redux and has
		//   no dependency on any other call, so there's no reason to make one
		//   wait on another — total load time becomes "as slow as the single
		//   slowest endpoint" instead of "the sum of every endpoint added up".
		//   A failed call is caught individually so one bad endpoint never
		//   blocks or delays the rest.
		// Cache key in Redux prevents duplicate calls on tab return.
		// ─────────────────────────────────────────────────────────────────────
		async fetchChartApis(payload: { startDate: string; endDate: string }, rootState: any) {
			const cacheKey = `${payload.startDate}_${payload.endDate}`;

			// Skip if this exact date range was already fetched (tab return guard)
			if (rootState.overview.lastFetchedKey === cacheKey) return;
			dispatch.overview.setLastFetchedKey(cacheKey);

			// Build KPI tile date range: yesterday → today (live window)
			const now = new Date();
			const yd = new Date(now.getTime() - 86_400_000);
			const fmt = (d: Date) =>
				`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
					d.getDate(),
				).padStart(2, '0')}T00:00:00`;
			const kpiDate = { startDate: fmt(yd), endDate: fmt(now) };

			// Mark KPI as fetched before firing so a second effect run mid-flight
			// doesn't double-fetch the tiles
			const isFirstLoad = !rootState.overview.kpiFetched;
			if (isFirstLoad) {
				dispatch.overview.setKpiFetched(true);
			}

			// ── One task list — every entry is a lazy thunk so none of them
			// starts until we call it below, all at once. ───────────────────
			const tasks: Array<() => Promise<unknown>> = [
				// ── KPI tiles (top row) — fixed yesterday→today window, first load only
				...(isFirstLoad
					? [
							() => dispatch.overview.getActiveVehicles(kpiDate),
							() => dispatch.overview.getActiveAlertsCount(kpiDate),
							() => dispatch.overview.getFleetUtilizationPercent(kpiDate),
							() => dispatch.overview.getDistanceCovered(kpiDate),
							() => dispatch.overview.getAvgFuelEfficiencyCount(kpiDate),
					  ]
					: []),

				// ── Section 1 — Location & Movement
				() => dispatch.overview.getTripDistanceKm(payload),
				() => dispatch.overview.getFleetUtilizationRate(payload),
				...(isFirstLoad ? [() => dispatch.overview.getVehicleTypeUtilization()] : []),
				() => dispatch.overview.getIdleTimeMin(payload),
				() => dispatch.overview.getStopDuration(payload),
				() => dispatch.overview.getVehicleAlertsV2(payload),
				() => dispatch.overview.getNoOfTrips(payload),

				// ── Section 2 — Fuel & Energy
				() => dispatch.overview.getDistanceDrivenKm(payload),
				() => dispatch.overview.getFuelConsumptionChart(payload),
				() => dispatch.overview.getIdleFuelWasteChart(payload),

				// ── Section 3 — Driver Behaviour
				() => dispatch.overview.getDriverScores(payload),
				() => dispatch.overview.getEcoDriverScore(payload),
				() =>
					dispatch.overview.getDriveVsIdle({
						startDate: payload.startDate,
						endDate: payload.endDate,
					}),

				// ── Section 4 — Safety & Alerts
				() => dispatch.overview.getDashcamAlertEvents(payload),
				() => dispatch.overview.getObdAlertEvents(payload),
				() => dispatch.overview.getDashcamAlertSeverity(payload),

				// ── Section 5 — Maintenance
				() => dispatch.overview.getMaintenanceStatusV2(),
				() => dispatch.overview.getMaintServiceOverdue(payload),

				// Driver name filter — populates a dropdown, not tied to any
				// visible chart, so it goes dead last regardless of section
				...(isFirstLoad ? [() => dispatch.overview.getDriverNameFilter()] : []),
			];
			const OVERVIEW_FETCH_CONCURRENCY = 6;
			await runWithConcurrencyLimit(tasks, OVERVIEW_FETCH_CONCURRENCY);
		},

		// ─────────────────────────────────────────────────────────────────────
		// Individual effects — kept intact so other pages / components that
		// call them directly continue to work without any change.
		// ─────────────────────────────────────────────────────────────────────

		async getOverviewVehiclesAlerts(payload) {
			// eslint-disable-next-line @typescript-eslint/naming-convention
			const { alert_type, sortorder } = payload;
			const { startDate, endDate } = payload.date;
			const payloadAlerts = generateRestAPI(
				[{ alert_type, sortorder }, { startdate: startDate }, { enddate: endDate }],
				process.env.REACT_APP_OVERVIEW_VEHICLE_ALERTS,
			);
			const getOverviewVehiclesAlertsResponse: IOverviewVehicleAlerts[] = await getData(
				payloadAlerts,
				'overview vehicles alerts',
			);
			if (Array.isArray(getOverviewVehiclesAlertsResponse)) {
				this.setOverviewVehicleAlerts(getOverviewVehiclesAlertsResponse);
			} else {
				this.setOverviewVehicleAlerts([]);
			}
			return getOverviewVehiclesAlertsResponse;
		},

		async getActiveVehicles(date: any) {
			const { startDate, endDate } = date;
			const payload = generateRestAPI(
				[{ startdate: startDate }, { enddate: endDate }],
				process.env.REACT_APP_OVERVIEW_ACTIVE_VEHICLES,
			);
			const res: any[] = await getData(payload, 'Active Vehicles');
			if (Array.isArray(res) && res.length > 0) {
				this.setActiveVehicles(res[0]);
			} else {
				this.setActiveVehicles(initialValues.activeVehicles);
			}
			return res;
		},

		async getActiveAlertsCount(date: any) {
			const { startDate, endDate } = date;
			const payload = generateRestAPI(
				[{ startdate: startDate }, { enddate: endDate }],
				process.env.REACT_APP_ACTIVE_ALERTS_COUNT,
			);
			const res: any[] = await getData(payload, 'Active Alerts Count');
			if (Array.isArray(res) && res.length > 0) {
				this.setActiveAlertsCount(res[0]);
			} else {
				this.setActiveAlertsCount(initialValues.activeAlertsCount);
			}
			return res;
		},

		async getAvgFuelEfficiencyCount(date: any, rootState: any) {
			const { startDate, endDate } = date;
			// Same fallback pattern used elsewhere in the app (see
			// src/services/dashcamService.js) when no org is set on the user.
			const organisationId = rootState?.auth?.customProperties?.organisationId || '1,2';
			const payload = generateRestAPI(
				[
					{ startdate: startDate },
					{ enddate: endDate },

					{ organisation_id: organisationId },
				],
				process.env.REACT_APP_OVERVIEW_FUEL_TOTAL_EFFICIENCY_COUNT,
			);
			const res: any[] = await getData(payload, 'Avg Fuel Efficiency Count');
			if (Array.isArray(res) && res.length > 0) {
				this.setAvgFuelEfficiencyCount(res[0]);
			} else {
				this.setAvgFuelEfficiencyCount(initialValues.avgFuelEfficiencyCount);
			}
			return res;
		},

		async getFleetUtilizationPercent(date: any) {
			const { startDate, endDate } = date;
			const payload = generateRestAPI(
				[{ startdate: startDate }, { enddate: endDate }],
				process.env.REACT_APP_OVERVIEW_FLEET_UTILIZATION,
			);
			const res: any[] = await getData(payload, 'Fleet Utilization Percent');
			if (Array.isArray(res) && res.length > 0) {
				this.setFleetUtilizationPercent(res[0]);
			} else {
				this.setFleetUtilizationPercent(initialValues.fleetUtilizationPercent);
			}
			return res;
		},

		async getDistanceCovered(date: any) {
			const { startDate, endDate } = date;
			const payload = generateRestAPI(
				[{ startdate: startDate }, { enddate: endDate }],
				process.env.REACT_APP_DISTANCE_DRIVEN,
			);
			const res: any[] = await getData(payload, 'Distance Covered');
			if (Array.isArray(res) && res.length > 0) {
				this.setDistanceCovered(res[0]);
			} else {
				this.setDistanceCovered(null);
			}
			return res;
		},

		async getTripDistanceKm(date: any) {
			const { startDate, endDate } = date;
			const payload = generateRestAPI(
				[{ startdate: startDate }, { enddate: endDate }],
				process.env.REACT_APP_OVERVIEW_TRIP_DISTANCE_KM,
			);
			const res: any[] = await getData(payload, 'Trip Distance Km');
			if (Array.isArray(res) && res.length > 0) {
				this.setTripDistanceKm(res[0]);
			} else {
				this.setTripDistanceKm(initialValues.tripDistanceKm);
			}
			return res;
		},

		async getFleetUtilizationRate(date: any) {
			const { startDate, endDate } = date;
			const payload = generateRestAPI(
				[{ startdate: startDate }, { enddate: endDate }],
				process.env.REACT_APP_OVERVIEW_FLEET_UTILIZATION_RATE,
			);
			const res: any[] = await getData(payload, 'Fleet Utilization Rate');
			if (Array.isArray(res) && res.length > 0) {
				this.setFleetUtilizationRate(res[0]);
			} else {
				this.setFleetUtilizationRate(initialValues.fleetUtilizationRate);
			}
			return res;
		},

		async getIdleTimeMin(date: any) {
			const { startDate, endDate } = date;
			const payload = generateRestAPI(
				[{ startdate: startDate }, { enddate: endDate }],
				process.env.REACT_APP_OVERVIEW_IDLE_TIME_HRS,
			);
			const res: any[] = await getData(payload, 'Idle Time Min');
			if (Array.isArray(res) && res.length > 0) {
				this.setIdleTimeMin(res[0]);
			} else {
				this.setIdleTimeMin(initialValues.idleTimeMin);
			}
			return res;
		},

		async getStopDuration(date: any) {
			const { startDate, endDate } = date;
			const payload = generateRestAPI(
				[{ startdate: startDate }, { enddate: endDate }],
				process.env.REACT_APP_OVERVIEW_STOP_DURATION,
			);
			const res: any[] = await getData(payload, 'Stop Duration');
			if (Array.isArray(res) && res.length > 0) {
				this.setStopDuration(res[0]);
			} else {
				this.setStopDuration(initialValues.stopDuration);
			}
			return res;
		},

		async getNoOfTrips(date: any) {
			const now = new Date();
			const fmt = (d: Date) =>
				`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
					d.getDate(),
				).padStart(2, '0')}`;
			const { startDate, endDate } = date;
			const payload = generateRestAPI(
				[
					{ startdate: startDate },
					{ enddate: endDate },
					{ todaystartdate: `${fmt(now)}T00:00:00` },
					{ todayenddate: `${fmt(now)}T23:59:59` },
				],
				process.env.REACT_APP_OVERVIEW_NO_OF_TRIPS,
			);
			const res: any[] = await getData(payload, 'No of Trips');
			if (Array.isArray(res) && res.length > 0) {
				this.setNoOfTrips(res[0]);
			} else {
				this.setNoOfTrips(initialValues.noOfTrips);
			}
			return res;
		},

		async getVehicleAlertsV2(date: any) {
			const { startDate, endDate } = date;
			const payload = generateRestAPI(
				[
					{ startdate: startDate },
					{ enddate: endDate },
					{ alert_type: 'All Alerts' },
					{ sortorder: -1 },
				],
				process.env.REACT_APP_OVERVIEW_VEHICLE_ALERTS_V2,
			);
			const res: any[] = await getData(payload, 'Vehicle Alerts V2');
			if (Array.isArray(res) && res.length > 0) {
				this.setVehicleAlertsV2(res);
			} else {
				this.setVehicleAlertsV2([]);
			}
			return res;
		},

		async getDistanceDrivenKm(date: any) {
			const { startDate, endDate } = date;
			const payload = generateRestAPI(
				[{ startdate: startDate }, { enddate: endDate }],
				process.env.REACT_APP_OVERVIEW_DISTANCE_DRIVEN_KM,
			);
			const res: any[] = await getData(payload, 'Distance Driven Km');
			if (Array.isArray(res) && res.length > 0) {
				this.setDistanceDrivenKm(res[0]);
			} else {
				this.setDistanceDrivenKm(initialValues.distanceDrivenKm);
			}
			return res;
		},

		async getVehicleTypeUtilization(_: void) {
			const payload = generateRestAPI(
				[],
				process.env.REACT_APP_OVERVIEW_VEHICLE_TYPE_UTILIZATION,
			);
			const res: any[] = await getData(payload, 'Vehicle Type Utilization');
			if (Array.isArray(res) && res.length > 0) {
				this.setVehicleTypeUtilization(res[0]?.vehicle_utilization ?? []);
			} else {
				this.setVehicleTypeUtilization([]);
			}
			return res;
		},

		async getFuelConsumptionChart(date: any) {
			const { startDate, endDate } = date;
			const payload = generateRestAPI(
				[{ startdate: startDate }, { enddate: endDate }],
				process.env.REACT_APP_OVERVIEW_FUEL_CONSUMPTION_CHART,
			);
			const res: any[] = await getData(payload, 'Fuel Consumption Chart');
			if (Array.isArray(res) && res.length > 0) {
				this.setFuelConsumptionChart(res[0]);
			} else {
				this.setFuelConsumptionChart(null);
			}
			return res;
		},

		async getIdleFuelWasteChart(date: any) {
			const { startDate, endDate } = date;
			const payload = generateRestAPI(
				[{ startdate: startDate }, { enddate: endDate }],
				process.env.REACT_APP_OVERVIEW_IDLE_FUEL_WASTE,
			);
			const res: any[] = await getData(payload, 'Idle Fuel Waste Chart');
			if (Array.isArray(res) && res.length > 0) {
				this.setIdleFuelWasteChart(res[0]);
			} else {
				this.setIdleFuelWasteChart(null);
			}
			return res;
		},

		async getDriverScores(date: any) {
			const { startDate, endDate } = date;
			const payload = generateRestAPI(
				[{ startdate: startDate }, { enddate: endDate }],
				process.env.REACT_APP_OVERVIEW_DRIVER_SCORE,
			);
			const res: any[] = await getData(payload, 'Driver Scores');
			if (Array.isArray(res) && res.length > 0) {
				this.setDriverScores(res);
			} else {
				this.setDriverScores([]);
			}
			return res;
		},

		async getEcoDriverScore(date: any) {
			const { startDate, endDate } = date;
			const payload = generateRestAPI(
				[{ startdate: startDate }, { enddate: endDate }],
				process.env.REACT_APP_OVERVIEW_ECO_DRIVER_SCORE,
			);
			const res: any[] = await getData(payload, 'Eco Driver Score');
			if (Array.isArray(res) && res.length > 0) {
				this.setEcoDriverScore(res[0]);
			} else {
				this.setEcoDriverScore(null);
			}
			return res;
		},

		async getDriverNameFilter(_: void, rootState: any) {
			const fleetId = rootState?.auth?.customProperties?.fleetId ?? 'All Fleets';
			const payload = generateRestAPI(
				[{ fleet_id: fleetId }, { vin: 'All Vins' }],
				process.env.REACT_APP_OVERVIEW_DRIVER_NAME_FILTER,
			);
			const res: any[] = await getData(payload, 'Driver Name Filter');
			if (Array.isArray(res) && res.length > 0) {
				this.setDriverNameFilter(res);
			} else {
				this.setDriverNameFilter([]);
			}
			return res;
		},

		async getDriveVsIdle(payload: any) {
			const { startDate, endDate, driverName } = payload;
			const isFiltered = driverName && driverName !== 'All Drivers';
			const params: any[] = [
				{ startdate: startDate },
				{ enddate: endDate },
				{ driver_name: isFiltered ? driverName : 'All Drivers' },
			];
			const p = generateRestAPI(params, process.env.REACT_APP_OVERVIEW_DRIVE_VS_IDLE);
			const res: any[] = await getData(p, 'Drive vs Idle');
			if (Array.isArray(res) && res.length > 0) {
				this.setDriveVsIdle(res);
				if (!isFiltered) {
					this.setDriveVsIdleAllDrivers(res);
				}
			} else {
				this.setDriveVsIdle([]);
				if (!isFiltered) {
					this.setDriveVsIdleAllDrivers([]);
				}
			}
			return res;
		},

		async getObdAlertEvents(date: any) {
			const { startDate, endDate } = date;
			const payload = generateRestAPI(
				[{ startdate: startDate }, { enddate: endDate }],
				process.env.REACT_APP_OVERVIEW_OBD_ALERT_EVENTS,
			);
			const res: any[] = await getData(payload, 'OBD Alert Events');
			if (Array.isArray(res) && res.length > 0) {
				this.setObdAlertEvents(res);
			} else {
				this.setObdAlertEvents([]);
			}
			return res;
		},

		async getDashcamAlertEvents(date: any) {
			const { startDate, endDate } = date;
			const payload = generateRestAPI(
				[{ startdate: startDate }, { enddate: endDate }],
				process.env.REACT_APP_OVERVIEW_DASHCAM_ALERT_EVENTS,
			);
			const res: any[] = await getData(payload, 'Dashcam Alert Events');
			if (Array.isArray(res) && res.length > 0) {
				this.setDashcamAlertEvents(res);
			} else {
				this.setDashcamAlertEvents([]);
			}
			return res;
		},

		async getDashcamAlertSeverity(date: any) {
			const { startDate, endDate } = date;
			const payload = generateRestAPI(
				[{ startdate: startDate }, { enddate: endDate }],
				process.env.REACT_APP_OVERVIEW_DASHCAM_ALERT_SEVERITY,
			);
			const res: any[] = await getData(payload, 'Dashcam Alert Severity');
			if (Array.isArray(res) && res.length > 0) {
				this.setDashcamAlertSeverity(res);
			} else {
				this.setDashcamAlertSeverity([]);
			}
			return res;
		},

		async getMaintenanceStatusV2(_: void) {
			const payload = generateRestAPI(
				[],
				process.env.REACT_APP_OVERVIEW_MAINTENANCE_STATUS_V2,
			);
			const res: any[] = await getData(payload, 'Maintenance Status V2');
			if (Array.isArray(res) && res.length > 0) {
				this.setMaintenanceStatusV2(res[0]);
			} else {
				this.setMaintenanceStatusV2(null);
			}
			return res;
		},

		async getMaintServiceOverdue(date: any) {
			const { startDate, endDate } = date;
			const payload = generateRestAPI(
				// [{ startdate: startDate }, { enddate: endDate }],
				[],
				process.env.REACT_APP_OVERVIEW_MAINT_SERVICE_OVERDUE,
			);
			const res: any[] = await getData(payload, 'Maint Service Overdue');
			if (Array.isArray(res) && res.length > 0) {
				this.setMaintServiceOverdue(res[0]);
			} else {
				this.setMaintServiceOverdue(null);
			}
			return res;
		},
	}),
});
